import type { DateTime } from 'luxon'
import { getPortalLabel, getTerminalLabel } from '~/constants'
import type {
  GETVesselSchedule,
  PortalName,
  ShippingLine,
  TerminalName,
  VesselScheduleField,
} from '~/services/apiClient'
import type {
  ExportScheduleDateChange,
  ExportScheduleDateField,
} from '~/models/exports'
import {
  parseScheduleChanges,
  scheduleChangeFor,
} from '~/models/scheduleDateChanges'
import { fastParseISO, formatDate, formatDTMilitary } from '~/utils'

/** Receiving-window status of a vessel schedule, driving the Exports board. */
export const VESSEL_SCHEDULE_STATUSES = [
  'scheduled',
  'open',
  'departed',
] as const
export type VesselScheduleStatus = (typeof VESSEL_SCHEDULE_STATUSES)[number]

/** Display labels — one source of truth for the board badge and the filter chips. */
export const VESSEL_SCHEDULE_STATUS_LABELS: Record<
  VesselScheduleStatus,
  string
> = {
  scheduled: 'Scheduled',
  open: 'Open',
  departed: 'Departed',
}

/**
 * The Exports Schedule board's window (DEV-7543), enforced server-side by
 * `GET /vessels/etas/?before=`. The day counts mirror the backend's
 * `SCHEDULED_WINDOW_DAYS` / `DEPARTED_WINDOW_DAYS` in `vessels_repo.py`;
 * `vesselSchedule.test.ts` fails if they drift.
 */
export const EXPORTS_LOOKBACK_DAYS = 7
export const EXPORTS_LOOKAHEAD_DAYS = 7
export const EXPORTS_DEPARTED_DAYS = 14
export const EXPORTS_REFRESH_MS = 60_000

/**
 * What each status includes, given that window. Shown as chip hover text and the
 * always-visible hint under the chips, so users know why a vessel is or isn't listed.
 */
export const VESSEL_SCHEDULE_STATUS_DESCRIPTIONS: Record<
  VesselScheduleStatus,
  string
> = {
  scheduled: `typically ETA within the next ${EXPORTS_LOOKAHEAD_DAYS} days, plus vessels with your active bookings`,
  open: 'receiving window is open (between ERD and cutoff)',
  departed: `typically vessels whose receiving window closed within the last ${EXPORTS_DEPARTED_DAYS} days`,
}

/** Line under the status chips: what the picked statuses include (none picked = all). */
export function exportsWindowHint(selected: VesselScheduleStatus[]): string {
  const statuses = selected.length
    ? VESSEL_SCHEDULE_STATUSES.filter((status) => selected.includes(status))
    : VESSEL_SCHEDULE_STATUSES
  return statuses
    .map(
      (status) =>
        `${VESSEL_SCHEDULE_STATUS_LABELS[status]}: ${VESSEL_SCHEDULE_STATUS_DESCRIPTIONS[status]}`
    )
    .join(' · ')
}

/**
 * Element Plus color per status — one source of truth for the board status badge
 * (ElTag) and the status filter chips (el-check-tag): scheduled = blue, open =
 * yellow, departed = gray.
 */
export const VESSEL_SCHEDULE_STATUS_TAG_TYPE: Record<
  VesselScheduleStatus,
  'primary' | 'warning' | 'info'
> = {
  scheduled: 'primary',
  open: 'warning',
  departed: 'info',
}

/**
 * Display label per tracked schedule field — one source of truth for the board's
 * column headers (uppercased) and the change-log panel's per-change rows, so the
 * two surfaces can't drift on what a field is called.
 *
 * Keyed by `VesselScheduleField`, the enum the change-log API diffs on, so adding
 * a tracked field backend-side fails the build here until it gets a label.
 */
export const VESSEL_SCHEDULE_FIELD_LABELS: Record<VesselScheduleField, string> =
  {
    eta: 'ETA',
    erd: 'ERD',
    first_available_date: 'First Available Date',
    cargo_cutoff: 'Cargo Cutoff',
    documentation_cutoff: 'Documentation Cutoff',
    reefer_cutoff: 'Reefer Cutoff',
    hazmat_cutoff: 'Hazmat Cargo Cutoff',
  }

const ERD_ZONE = 'America/Los_Angeles'

/**
 * Whether an ERD carries a time. Terminals that publish only a date (the
 * Tideworks ones: Pier A, PCT, OICT) are stored at Pacific midnight, and no
 * terminal opens receiving at midnight.
 */
export function erdHasTime(erd: DateTime): boolean {
  const local = erd.setZone(ERD_ZONE)
  return local.hour !== 0 || local.minute !== 0
}

/**
 * An ERD as "10/27 07:00", or "10/27" when only the date is known. A date-only
 * ERD is a Pacific date, so it is read there whatever the viewer's zone.
 */
export function formatErd(erd: DateTime): string {
  return erdHasTime(erd)
    ? formatDTMilitary(erd)
    : formatDate(erd.setZone(ERD_ZONE))
}

/**
 * The two dates the receiving-window rule reads.
 *
 * Declared as its own shape so the rule can be applied to anything carrying a
 * receiving window, not just `VesselSchedule`. `GETVesselSchedule` (the board)
 * and `VesselScheduleForExport` (the Exports list) are different payloads —
 * the former requires `id`/`eta`/`portal_name`, the latter only `terminal` —
 * so the two cannot share a class, but they must not grow two rules.
 */
export interface VesselReceivingWindow {
  erd: DateTime | undefined
  cargoCutoff: DateTime | undefined
}

/**
 * Receiving-window status relative to `now` — the one rule behind the Exports
 * schedule board, its status filter, and the Exports list.
 *
 * Provisional rules (pending product sign-off — DEV-7495):
 * - `departed`  — cargo cutoff has passed (receiving closed)
 * - `open`      — earliest receiving date reached, cutoff not yet passed
 * - `scheduled` — everything else (receiving hasn't opened, or no ERD known)
 *
 * `now` is passed in rather than read from the clock so a single render
 * classifies every row against the same instant and the rule stays testable.
 */
export function receivingWindowStatus(
  window: VesselReceivingWindow,
  now: DateTime
): VesselScheduleStatus {
  if (window.cargoCutoff && now > window.cargoCutoff) {
    return 'departed'
  }
  if (window.erd && now >= window.erd) {
    return 'open'
  }
  return 'scheduled'
}

export class VesselSchedule {
  /**
   * The `vessel_schedules` row id — one row per (vessel, portal, terminal), so it
   * uniquely identifies a single board row. Used as the board's row key; the
   * change-log endpoint is keyed on (vessel name, terminal) rather than this.
   */
  id: number
  eta: DateTime
  firstObserved: DateTime
  lastObserved: DateTime
  portal: PortalName
  terminal: TerminalName | undefined
  voyageNumber: string | undefined
  /**
   * The carrier, when the terminal's feed names one. Unset for terminals whose
   * feed carries none (ITS, Everport) and for those whose line we can't pick yet
   * (APM, TraPac list several) — the board and filter treat that as "Unknown".
   */
  shippingLine: ShippingLine | undefined
  /** Earliest Receiving Date — date-only (no time/zone); serialized as "YYYY-MM-DD". */
  erd: DateTime | undefined
  /**
   * Earliest day empties can be picked up for this vessel — date-only, for
   * information. Only Everport, FMS and TTI send it.
   */
  firstAvailableDate: DateTime | undefined
  cargoCutoff: DateTime | undefined
  documentationCutoff: DateTime | undefined
  reeferCutoff: DateTime | undefined
  hazmatCutoff: DateTime | undefined
  /**
   * When this schedule's ERD or a cutoff last moved, if inside the backend's
   * recent-change window. Read it through `changeFor`; see
   * `ExportVesselSchedule`, which carries the same fact for the Exports list.
   */
  changedAt: DateTime | undefined
  /**
   * Public only because a private member is dropped when Vue unwraps an
   * instance held in a store (see `ExportVesselSchedule.previousDates`).
   */
  readonly previousDates: Partial<Record<ExportScheduleDateField, DateTime>>
  constructor(raw: GETVesselSchedule) {
    this.id = raw.id
    this.eta = fastParseISO(raw.eta)
    this.firstObserved = fastParseISO(raw.first_observed)
    this.lastObserved = fastParseISO(raw.last_observed!)
    this.portal = raw.portal_name
    this.terminal = raw.terminal
    this.voyageNumber = raw.voyage_number
    this.shippingLine = raw.shipping_line ?? undefined
    this.erd = raw.erd ? fastParseISO(raw.erd) : undefined
    this.firstAvailableDate = raw.first_available_date
      ? fastParseISO(raw.first_available_date)
      : undefined
    this.cargoCutoff = raw.cargo_cutoff
      ? fastParseISO(raw.cargo_cutoff)
      : undefined
    this.documentationCutoff = raw.documentation_cutoff
      ? fastParseISO(raw.documentation_cutoff)
      : undefined
    this.reeferCutoff = raw.reefer_cutoff
      ? fastParseISO(raw.reefer_cutoff)
      : undefined
    this.hazmatCutoff = raw.hazmat_cutoff
      ? fastParseISO(raw.hazmat_cutoff)
      : undefined
    const changes = parseScheduleChanges(raw)
    this.changedAt = changes.changedAt
    this.previousDates = changes.previousDates
  }

  /** See `scheduleChangeFor`. */
  changeFor(
    field: ExportScheduleDateField
  ): ExportScheduleDateChange | undefined {
    return scheduleChangeFor(this, field)
  }

  get portalLabel(): string {
    return getPortalLabel(this.portal)
  }

  get terminalLabel(): string {
    if (!this.terminal) {
      return ''
    }
    return getTerminalLabel(this.terminal)
  }

  /** See `receivingWindowStatus` — this is that rule, not a second copy of it. */
  getStatus(now: DateTime): VesselScheduleStatus {
    return receivingWindowStatus(this, now)
  }
}
